<template>
  <div class="widget widget-recent-activity">
    <div class="widget-heading">
      <h5>Recent Activities</h5>
      <!-- Real-time status indicator -->
      <div class="activity-status">
        <span 
          class="status-dot" 
          :class="{ 'online': isSocketConnected, 'offline': !isSocketConnected }"
          :title="isSocketConnected ? 'Real-time updates active' : 'Real-time updates disconnected'"
        ></span>
        <small class="status-text">
          {{ isSocketConnected ? 'Live' : 'Offline' }}
        </small>
      </div>
    </div>
    <div class="widget-content">
      <perfect-scrollbar class="timeline-line">
        <template v-if="loading">
          <!-- Loading state -->
          <div class="text-center p-3">
            <div class="spinner-border spinner-border-sm" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-2 mb-0">Loading activities...</p>
          </div>
        </template>
        
        <template v-else-if="!activities || activities.length === 0">
          <!-- No activities state -->
          <div class="text-center p-3">
            <p class="mb-0 text-muted">No recent activities today</p>
          </div>
        </template>
        
        <template v-else>
          <!-- Activities list -->
          <div 
            v-for="(activity, index) in activities" 
            :key="activity.id || index" 
            class="item-timeline" 
            :class="`timeline-${getActivityTypeClass(activity.type, activity.action)}`"
          >
            <div class="badge" :class="`badge-${getActivityTypeClass(activity.type, activity.action)}`"></div>
            <div class="t-text">
              <p v-html="activity.message"></p>
              <b-badge 
                :variant="`outline-${getActivityTypeClass(activity.type, activity.action)}`"
                v-if="getActivityStatus(activity)"
              >
                {{ getActivityStatus(activity) }}
              </b-badge>
              <p class="t-time">{{ formatTime(activity.time) }}</p>
            </div>
          </div>
        </template>
      </perfect-scrollbar>

      <div class="tm-action-btn">
        <b-button variant="default" @click="viewAllActivities">
          <span>View All</span>
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            stroke-width="2" 
            stroke-linecap="round" 
            stroke-linejoin="round" 
            class="feather feather-arrow-right"
          >
            <line x1="5" y1="12" x2="19" y2="12"></line>
            <polyline points="12 5 19 12 12 19"></polyline>
          </svg>
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import '@/assets/sass/widgets/widgets.scss';
import io from 'socket.io-client';

export default {
  name: 'RecentActivityWidget',
  data() {
    return {
      loading: false,
      socket: null,
      isSocketConnected: false,
      reconnectAttempts: 0,
      maxReconnectAttempts: 5
    };
  },
  computed: {
    activities() {
      return this.$store.state.recentActivities || [];
    },
    socketUrl() {
      return process.env.VUE_APP_ENV === 'production' 
        ? process.env.VUE_APP_API_URL 
        : process.env.VUE_APP_LOCAL_REALTIME_URL;
    }
  },
  async mounted() {
    // Clear old activities and fetch fresh data
    this.$store.dispatch('clearOldActivities');
    await this.fetchRecentActivities();
    
    // Set up socket connection for real-time updates
    this.setupSocketConnection();
  },
  beforeDestroy() {
    // Clean up socket connection
    this.disconnectSocket();
  },
  methods: {
    async fetchRecentActivities() {
      try {
        this.loading = true;
        await this.$store.dispatch('fetchRecentActivities');
      } catch (error) {
        console.error('Error fetching recent activities:', error);
      } finally {
        this.loading = false;
      }
    },
    
    setupSocketConnection() {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No auth token found for socket connection');
        return;
      }

      // Configure socket options based on environment
      let socketOptions = {
        query: { token },
        transports: ['websocket'],
        timeout: 20000,
        forceNew: true
      };

      if (process.env.VUE_APP_ENV === 'production') {
        socketOptions.path = "/realtime/socket.io/";
      }

      // Create socket connection
      this.socket = io(this.socketUrl, socketOptions);

      // Socket event listeners
      this.socket.on('connect', () => {
        console.log('🟢 Socket connected for activity updates');
        this.isSocketConnected = true;
        this.reconnectAttempts = 0;
      });

      this.socket.on('disconnect', (reason) => {
        console.log('🔴 Socket disconnected:', reason);
        this.isSocketConnected = false;
        
        // Auto-reconnect for certain disconnect reasons
        if (reason === 'io server disconnect') {
          // Server initiated disconnect, reconnect manually
          this.attemptReconnect();
        }
      });

      this.socket.on('connect_error', (error) => {
        console.error('❌ Socket connection error:', error);
        this.isSocketConnected = false;
        this.attemptReconnect();
      });

      // Listen for new activities
      this.socket.on('activity:new', (activity) => {
        console.log('🔥 New activity received:', activity);
        this.$store.dispatch('addRecentActivity', activity);
        
        // Show a brief visual feedback for new activity
        this.showNewActivityFeedback();
      });

      // Listen for reconnect events
      this.socket.on('reconnect', (attemptNumber) => {
        console.log('🔄 Socket reconnected after', attemptNumber, 'attempts');
        this.isSocketConnected = true;
        this.reconnectAttempts = 0;
      });
    },

    attemptReconnect() {
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        console.log(`🔄 Attempting to reconnect socket (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
        
        setTimeout(() => {
          if (this.socket) {
            this.socket.connect();
          }
        }, Math.pow(2, this.reconnectAttempts) * 1000); // Exponential backoff
      } else {
        console.warn('⚠️ Max reconnection attempts reached');
      }
    },

    disconnectSocket() {
      if (this.socket) {
        this.socket.off('activity:new');
        this.socket.off('connect');
        this.socket.off('disconnect');
        this.socket.off('connect_error');
        this.socket.off('reconnect');
        this.socket.disconnect();
        this.socket = null;
        this.isSocketConnected = false;
      }
    },

    showNewActivityFeedback() {
      // Add a brief visual effect to indicate new activity
      const widget = this.$el.querySelector('.widget-recent-activity');
      if (widget) {
        widget.classList.add('new-activity-pulse');
        setTimeout(() => {
          widget.classList.remove('new-activity-pulse');
        }, 1000);
      }
    },
    
    viewAllActivities() {
      // Navigate to activities page or show modal with all activities
      console.log('View all activities clicked');
      // Future: Implement full activities view
      // this.$router.push({ name: 'activities' });
    },
    
    getActivityTypeClass(type, action) {
      // Map new activity types to Bootstrap variant classes
      const typeActionMap = {
        // Employee activities
        'employee': {
          'created': 'success',
          'updated': 'primary',
          'left': 'danger',
          'default': 'primary'
        },
        // Attendance activities
        'attendance': {
          'marked': 'info',
          'checked_in': 'success',
          'checked_out': 'warning',
          'replaced': 'secondary',
          'default': 'info'
        },
        // Leave activities
        'leave': {
          'requested': 'warning',
          'approved': 'success',
          'rejected': 'danger',
          'default': 'warning'
        },
        // Inventory activities
        'inventory': {
          'requested': 'info',
          'issued': 'success',
          'cancelled': 'danger',
          'returned': 'secondary',
          'default': 'info'
        },
        // Auth activities
        'auth': {
          'login': 'primary',
          'logout': 'secondary',
          'register': 'success',
          'password_reset': 'warning',
          'otp_verified': 'info',
          'default': 'primary'
        }
      };
      
      if (typeActionMap[type]) {
        return typeActionMap[type][action] || typeActionMap[type]['default'];
      }
      
      return 'primary';
    },

    getActivityStatus(activity) {
      // Extract status from metadata or use default based on type/action
      if (activity.metadata && activity.metadata.status) {
        return activity.metadata.status;
      }

      // Default status based on action
      const statusMap = {
        'created': 'Created',
        'updated': 'Updated', 
        'left': 'Left',
        'marked': 'Marked',
        'checked_in': 'Checked In',
        'checked_out': 'Checked Out',
        'replaced': 'Replaced',
        'requested': 'Requested',
        'approved': 'Approved',
        'rejected': 'Rejected',
        'issued': 'Issued',
        'cancelled': 'Cancelled',
        'returned': 'Returned',
        'login': 'Logged In',
        'logout': 'Logged Out',
        'register': 'Registered',
        'password_reset': 'Password Reset',
        'otp_verified': 'OTP Verified'
      };

      return statusMap[activity.action] || 'Completed';
    },
    
    formatTime(timeString) {
      try {
        const now = new Date();
        const activityTime = new Date(timeString);
        const diffInMinutes = Math.floor((now - activityTime) / (1000 * 60));
        
        if (diffInMinutes < 1) {
          return 'Just now';
        } else if (diffInMinutes < 60) {
          return `${diffInMinutes} min ago`;
        } else if (diffInMinutes < 1440) { // Less than 24 hours
          const hours = Math.floor(diffInMinutes / 60);
          return `${hours} hour${hours > 1 ? 's' : ''} ago`;
        } else {
          // Format as time for same day activities
          return activityTime.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
          });
        }
      } catch (error) {
        return timeString;
      }
    }
  }
};
</script>

<style scoped>
/* Additional component-specific styles */
.widget-recent-activity {
  position: relative;
}

.widget-recent-activity .widget-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.activity-status {
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  transition: all 0.3s ease;
}

.status-dot.online {
  background-color: #1abc9c;
  box-shadow: 0 0 8px rgba(26, 188, 156, 0.6);
  animation: pulse-green 2s infinite;
}

.status-dot.offline {
  background-color: #e74c3c;
  box-shadow: 0 0 8px rgba(231, 76, 60, 0.6);
}

.status-text {
  font-size: 0.75rem;
  font-weight: 500;
  color: #888ea8;
}

.status-text {
  color: #1abc9c;
}

.status-dot.offline + .status-text {
  color: #e74c3c;
}

@keyframes pulse-green {
  0% {
    box-shadow: 0 0 0 0 rgba(26, 188, 156, 0.7);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(26, 188, 156, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(26, 188, 156, 0);
  }
}

.widget-recent-activity.new-activity-pulse {
  animation: activity-pulse 1s ease-in-out;
}

@keyframes activity-pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(26, 188, 156, 0.4);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 0 0 10px rgba(26, 188, 156, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(26, 188, 156, 0);
  }
}

.widget-recent-activity .timeline-line {
  max-height: 400px;
}

.widget-recent-activity .item-timeline {
  margin-bottom: 1rem;
}

.widget-recent-activity .t-text p {
  margin-bottom: 0.5rem;
}

.widget-recent-activity .t-time {
  font-size: 0.875rem;
  color: #888ea8;
  margin-bottom: 0;
}

.widget-recent-activity .tm-action-btn {
  text-align: center;
  margin-top: 1rem;
}

/* Timeline badge colors for different activity types */
.timeline-success .badge {
  background-color: #1abc9c;
}

.timeline-primary .badge {
  background-color: #3b82f6;
}

.timeline-danger .badge {
  background-color: #e74c3c;
}

.timeline-warning .badge {
  background-color: #f39c12;
}

.timeline-info .badge {
  background-color: #17a2b8;
}

.timeline-secondary .badge {
  background-color: #6c757d;
}
</style> 